import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, type Article, type Citation, type VerifyClaimResult } from "../lib/api.ts";
import { statusBadgeClass, statusLabel, formatDate, truncate } from "../lib/utils.ts";

export default function ArticleDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [article, setArticle]         = useState<Article | null>(null);
  const [citations, setCitations]     = useState<Citation[]>([]);
  const [loading, setLoading]         = useState(true);
  const [saving, setSaving]           = useState(false);

  // Editable fields
  const [editTitle, setEditTitle]     = useState("");
  const [editBody, setEditBody]       = useState("");
  const [editBluf, setEditBluf]       = useState("");
  const [editDeck, setEditDeck]       = useState("");
  const [editPullQuote, setEditPullQuote] = useState("");
  const [pullQuoteAccepted, setPullQuoteAccepted] = useState(false);
  const [styleMode, setStyleMode]     = useState<"a" | "b">("a");
  const [companionEnabled, setCompanionEnabled] = useState(false);
  const [showOriginalSource, setShowOriginalSource] = useState(false);
  const [selectedModel, setSelectedModel] = useState("Qwen/Qwen2.5-7B-Instruct");

  const modelOptions = [
    { value: "Qwen/Qwen2.5-7B-Instruct", label: "Qwen 7B (cheap)", cost: "~$0.0004/req" },
    { value: "meta-llama/Llama-3.1-8B-Instruct", label: "Llama 3.1 8B (mid)", cost: "~$0.0004/req" },
    { value: "moonshotai/Kimi-K2.6", label: "Kimi K2.6 (premium)", cost: "~$0.009/req" },
  ];

  // Veracity check state
  const [verifyClaim, setVerifyClaim]           = useState("");
  const [verifyContext, setVerifyContext]        = useState("");
  const [verifying, setVerifying]               = useState(false);
  const [verifyResult, setVerifyResult]         = useState<VerifyClaimResult | null>(null);
  const [showVerifyPanel, setShowVerifyPanel]   = useState(false);

  const articleId = parseInt(id ?? "0", 10);

  useEffect(() => {
    if (!articleId) return;
    api.articles.get(articleId)
      .then(r => {
        const a = r.article;
        setArticle(a);
        setCitations(r.citations);
        setEditTitle(a.rewritten_title ?? a.original_title);
        setEditBody(a.rewritten_body ?? "");
        setEditBluf(a.bluf_subhead ?? "");
        setEditDeck(a.deck ?? "");
        setEditPullQuote(a.pull_quote ?? "");
        setPullQuoteAccepted(a.pull_quote_accepted === 1);
        setStyleMode(a.style_mode ?? "a");
        setShowOriginalSource(a.show_original_source === 1);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [articleId]);

  async function saveEdits() {
    await api.articles.edit(articleId, {
      rewritten_title:    editTitle,
      rewritten_body:     editBody,
      bluf_subhead:       editBluf || null,
      deck:               editDeck || null,
      pull_quote:         editPullQuote || null,
      pull_quote_accepted: pullQuoteAccepted ? 1 : 0,
      style_mode:         styleMode,
      show_original_source: showOriginalSource ? 1 : 0,
    } as Partial<Article>);
  }

  async function handleApprove() {
    if (!article) return;
    setSaving(true);
    try {
      await saveEdits();
      await api.articles.approve(articleId);
      navigate("/review");
    } finally { setSaving(false); }
  }

  async function handleApproveDraft() {
    if (!article) return;
    setSaving(true);
    try {
      await saveEdits();
      await api.articles.approve(articleId, { draft: true });
      navigate("/review");
    } finally { setSaving(false); }
  }

  async function handleReject() {
    const note = prompt("Rejection reason:");
    if (note === null) return;
    await api.articles.reject(articleId, note);
    navigate("/review");
  }

  async function handleRewrite() {
    try {
      // Save current edits (including style_mode) so the backend uses the selected style
      await api.articles.edit(articleId, {
        rewritten_title:    editTitle,
        rewritten_body:     editBody,
        bluf_subhead:       editBluf || null,
        deck:               editDeck || null,
        pull_quote:         editPullQuote || null,
        pull_quote_accepted: pullQuoteAccepted ? 1 : 0,
        style_mode:         styleMode,
        show_original_source: showOriginalSource ? 1 : 0,
      } as Partial<Article>);
      // Then trigger the AI rewrite with the selected model
      await api.articles.rewrite(articleId, { model: selectedModel });
      if (article && isReviewable) {
        navigate("/review");
      } else {
        // Stay on page and refresh article data
        const r = await api.articles.get(articleId);
        setArticle(r.article);
      }
    } catch (e) {
      alert("Rewrite failed: " + (e instanceof Error ? e.message : String(e)));
    }
  }

  async function handleSave() {
    if (!article) return;
    setSaving(true);
    try {
      const updated = await api.articles.edit(articleId, {
        rewritten_title:    editTitle,
        rewritten_body:     editBody,
        bluf_subhead:       editBluf || null,
        deck:               editDeck || null,
        pull_quote:         editPullQuote || null,
        pull_quote_accepted: pullQuoteAccepted ? 1 : 0,
        style_mode:         styleMode,
        show_original_source: showOriginalSource ? 1 : 0,
      } as Partial<Article>);
      setArticle(updated);
    } finally { setSaving(false); }
  }

  
  async function handleStyleChange(mode: "a" | "b") {
    setStyleMode(mode);
    if (!isEditable) return;
    setSaving(true);
    try {
      await api.articles.edit(articleId, { style_mode: mode } as Partial<Article>);
      await api.articles.rewrite(articleId, { model: selectedModel });
      if (isReviewable) {
        navigate("/review");
      } else {
        const r = await api.articles.get(articleId);
        setArticle(r.article);
      }
    } finally { setSaving(false); }
  }

  async function handleVerify() {
    if (!verifyClaim.trim()) return;
    setVerifying(true);
    setVerifyResult(null);
    try {
      const result = await api.articles.verify_claim(articleId, verifyClaim, verifyContext || undefined);
      setVerifyResult(result);
    } catch (e) {
      setVerifyResult({
        verifiability: "unverifiable",
        notes: String(e),
        suggested_sources: [],
      });
    } finally { setVerifying(false); }
  }

  async function toggleCitation(citationId: number, include: boolean) {
    const updated = await api.citations.update(citationId, { publish_include: include ? 1 : 0 });
    setCitations(prev => prev.map(c => c.id === citationId ? updated : c));
  }

  if (loading) return <div className="p-6 text-gray-400 text-sm">Loading…</div>;
  if (!article) return <div className="p-6 text-gray-400 text-sm">Article not found</div>;

  const isEditable  = article.status === "pending_review" || article.status === "approved";
  const isReviewable = article.status === "pending_review";

  const verifyBadgeClass = (v: string) =>
    v === "verifiable"   ? "bg-green-100 text-green-700 border-green-200" :
    v === "contested"    ? "bg-yellow-100 text-yellow-700 border-yellow-200" :
                           "bg-red-100 text-red-700 border-red-200";

  return (
    <div className="p-6 max-w-5xl">
      {/* Header */}
      <div className="flex items-center gap-3 mb-5">
        <button onClick={() => navigate(-1)} className="btn-ghost btn btn-sm">← Back</button>
        <span className={statusBadgeClass(article.status)}>{statusLabel(article.status)}</span>
        <span className="badge-gray">{article.feed_name ?? `Feed ${article.feed_id}`}</span>
        <span className="badge-gray">{article.ghost_instance ?? "news"} ghost</span>
      </div>

      {/* Error notice */}
      {article.content_fetch_error && (
        <div className="mb-4 p-3 rounded-lg bg-red-50 border border-red-200 text-sm text-red-700">
          ⚠️ {article.content_fetch_error}
        </div>
      )}

      {/* Content incomplete notice */}
      {article.rss_excerpt_only === 1 && (
        <div className="mb-4 p-3 rounded-lg bg-orange-50 border border-orange-200 text-sm text-orange-700">
          ⚠️ Content fetching failed — article uses RSS excerpt only. Review carefully before approving.
        </div>
      )}

      {/* Style mode, companion, and model selector */}
      <div className="card p-4 mb-5 flex flex-wrap items-center gap-6">
        <fieldset className="flex items-center gap-3">
          <legend className="label mr-2">Style</legend>
          <label className="flex items-center gap-1.5 cursor-pointer">
            <input
              type="radio" name="style_mode" value="a"
              checked={styleMode === "a"}
              onChange={() => handleStyleChange("a")}
              disabled={!isEditable}
              className="text-brand-500"
            />
            <span className="text-sm font-medium">A — Intelligence Briefing</span>
          </label>
          <label className="flex items-center gap-1.5 cursor-pointer">
            <input
              type="radio" name="style_mode" value="b"
              checked={styleMode === "b"}
              onChange={() => handleStyleChange("b")}
              disabled={!isEditable}
              className="text-brand-500"
            />
            <span className="text-sm font-medium">B — Magazine Feature</span>
          </label>
        </fieldset>

        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={companionEnabled}
            onChange={e => setCompanionEnabled(e.target.checked)}
            disabled={!isEditable}
            className="rounded border-gray-300 text-brand-500"
          />
          <span className="text-sm">
            Generate companion {styleMode === "a" ? "Style B feature" : "Style A briefing"}
          </span>
        </label>

        <div className="flex items-center gap-2">
          <label className="text-sm text-gray-500">Model:</label>
          <select
            value={selectedModel}
            onChange={e => setSelectedModel(e.target.value)}
            className="input text-sm py-1 pr-6"
          >
            {modelOptions.map(m => (
              <option key={m.value} value={m.value}>
                {m.label}
              </option>
            ))}
          </select>
          <span className="text-xs text-gray-400">
            {modelOptions.find(m => m.value === selectedModel)?.cost}
          </span>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Original */}
        <div>
          <h2 className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-2">Original</h2>
          <div className="card p-4 space-y-3">
            <div>
              <p className="text-xs text-gray-400 mb-1">Title</p>
              <p className="font-medium text-gray-900">{article.original_title}</p>
            </div>
            {article.original_author && (
              <p className="text-xs text-gray-500">By {article.original_author}</p>
            )}
            {article.original_published_at && (
              <p className="text-xs text-gray-400">{formatDate(article.original_published_at)}</p>
            )}
            <div>
              <p className="text-xs text-gray-400 mb-1">
                Content ({article.content_fetch_source ?? "none"})
              </p>
              <div className="text-sm text-gray-700 max-h-64 overflow-y-auto whitespace-pre-wrap break-words">
                {article.original_body ? truncate(article.original_body, 1500) : "—"}
              </div>
            </div>
            <a
              href={article.original_url}
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs text-brand-500 hover:underline"
            >
              View original →
            </a>
          </div>
        </div>

        {/* Rewritten */}
        <div>
          <h2 className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-2 flex items-center gap-2">
            Rewritten {article.rewrite_model && (
              <span className="normal-case font-normal text-gray-400">— {article.rewrite_model}</span>
            )}
            {article.rewritten_body && (
              <button
                onClick={() => window.open(`/popout/${articleId}`, `article-${articleId}`, "width=1200,height=900,scrollbars=yes")}
                className="ml-auto btn-ghost btn btn-xs text-xs"
                title="Open rewritten article in full-width pop-out window"
              >
                Pop out
              </button>
            )}
          </h2>
          <div className="card p-4 space-y-3">
            <div>
              <label className="label">Headline</label>
              <input
                value={editTitle}
                onChange={e => setEditTitle(e.target.value)}
                className="input text-sm font-medium"
                disabled={!isEditable}
              />
            </div>

            {/* Style A: BLUF subhead */}
            {styleMode === "a" && (
              <div>
                <label className="label">BLUF Subhead</label>
                <input
                  value={editBluf}
                  onChange={e => setEditBluf(e.target.value)}
                  placeholder="Core fact + primary implication in one sentence"
                  className="input text-sm"
                  disabled={!isEditable}
                />
              </div>
            )}

            {/* Style B: Deck */}
            {styleMode === "b" && (
              <div>
                <label className="label">Deck (Subhead)</label>
                <textarea
                  value={editDeck}
                  onChange={e => setEditDeck(e.target.value)}
                  rows={2}
                  placeholder="One or two sentences expanding the headline"
                  className="input text-sm resize-none"
                  disabled={!isEditable}
                />
              </div>
            )}

            <div>
              <label className="label">Body (HTML)</label>
              <textarea
                value={editBody}
                onChange={e => setEditBody(e.target.value)}
                rows={14}
                className="input text-sm font-mono resize-y"
                disabled={!isEditable}
              />
            </div>

            {(isEditable) && (
              <button onClick={handleSave} disabled={saving} className="btn-ghost btn btn-sm">
                {saving ? "Saving…" : "Save edits"}
              </button>
            )}

            {article.rewrite_tokens && (
              <p className="text-xs text-gray-400">
                {article.rewrite_tokens.toLocaleString()} tokens
                {article.rewrite_cost_usd != null && ` · $${article.rewrite_cost_usd.toFixed(4)}`}
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Pull Quote — always visible regardless of style mode */}
      <div className="mt-6">
        <h2 className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-2">
          Pull Quote
          {!pullQuoteAccepted && editPullQuote && (
            <span className="ml-2 text-xs font-normal text-yellow-600">— not yet accepted</span>
          )}
          {pullQuoteAccepted && (
            <span className="ml-2 text-xs font-normal text-green-600">— accepted</span>
          )}
        </h2>
        <div className="card p-4 space-y-3">
          <textarea
            value={editPullQuote}
            onChange={e => setEditPullQuote(e.target.value)}
            rows={3}
            placeholder={styleMode === 'b'
              ? 'AI-suggested pull quote — review and accept, edit, or clear to remove'
              : 'Optional pull quote for this briefing — suggested by AI or entered manually'}
            className="input text-sm italic resize-none"
            disabled={!isEditable}
          />
          <div className="flex gap-3">
            {editPullQuote.trim() && !pullQuoteAccepted && isEditable && (
              <button
                onClick={() => setPullQuoteAccepted(true)}
                className="btn-primary btn btn-sm"
              >
                Accept pull quote
              </button>
            )}
            {pullQuoteAccepted && isEditable && (
              <button
                onClick={() => setPullQuoteAccepted(false)}
                className="btn-ghost btn btn-sm"
              >
                Un-accept
              </button>
            )}
            {editPullQuote.trim() && isEditable && (
              <button
                onClick={() => { setEditPullQuote(""); setPullQuoteAccepted(false); }}
                className="btn-ghost btn btn-sm text-red-500"
              >
                Remove
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Show original source toggle */}
      <div className="mt-6">
        <div className="card p-4">
          <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={showOriginalSource}
              onChange={e => setShowOriginalSource(e.target.checked)}
              disabled={!isEditable}
              className="rounded border-gray-300 text-brand-500"
            />
            <div>
              <span className="text-sm font-medium">Show original source link below article</span>
              {article.original_url && (
                <p className="text-xs text-gray-400 mt-0.5">
                  Readers see: <a href={article.original_url} target="_blank" rel="noopener noreferrer" className="text-brand-500 hover:underline">{article.original_url}</a>
                </p>
              )}
            </div>
          </label>
        </div>
      </div>

      {/* Veracity check panel */}
      <div className="mt-6">
        <div className="flex items-center gap-3 mb-2">
          <h2 className="text-sm font-medium text-gray-500 uppercase tracking-wider">
            Veracity Check
          </h2>
          <button
            onClick={() => setShowVerifyPanel(v => !v)}
            className="btn-ghost btn btn-sm text-xs"
          >
            {showVerifyPanel ? "Hide" : "Check a claim"}
          </button>
        </div>
        {showVerifyPanel && (
          <div className="card p-4 space-y-3">
            <div>
              <label className="label">Claim or sentence to verify</label>
              <textarea
                value={verifyClaim}
                onChange={e => setVerifyClaim(e.target.value)}
                rows={2}
                placeholder="Paste the claim, sentence, or statistic you want to check"
                className="input text-sm resize-none"
              />
            </div>
            <div>
              <label className="label">Context (optional — paste surrounding paragraph)</label>
              <textarea
                value={verifyContext}
                onChange={e => setVerifyContext(e.target.value)}
                rows={3}
                placeholder="Surrounding context from the article helps the AI give a more accurate assessment"
                className="input text-sm resize-none"
              />
            </div>
            <button
              onClick={handleVerify}
              disabled={verifying || !verifyClaim.trim()}
              className="btn-primary btn btn-sm"
            >
              {verifying ? "Checking…" : "Check veracity"}
            </button>

            {verifyResult && (
              <div className={`rounded-lg border p-3 text-sm ${verifyBadgeClass(verifyResult.verifiability)}`}>
                <p className="font-semibold capitalize mb-1">{verifyResult.verifiability}</p>
                <p className="mb-2">{verifyResult.notes}</p>
                {verifyResult.suggested_sources.length > 0 && (
                  <div>
                    <p className="font-medium text-xs mb-1">Suggested sources to check:</p>
                    <ul className="list-disc list-inside space-y-0.5">
                      {verifyResult.suggested_sources.map((s, i) => (
                        <li key={i} className="text-xs">{s}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Citations */}
      {citations.length > 0 && (
        <div className="mt-6">
          <h2 className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-2">
            Citations ({citations.filter(c => c.publish_include).length}/{citations.length} included)
          </h2>
          <div className="card divide-y divide-gray-100">
            {citations.map(c => (
              <div key={c.id} className="flex items-center gap-3 px-4 py-2.5">
                <input
                  type="checkbox"
                  checked={c.publish_include === 1}
                  onChange={e => toggleCitation(c.id, e.target.checked)}
                  className="rounded border-gray-300 text-brand-500"
                />
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-gray-800 truncate">{c.source_text}</p>
                  {c.source_url && (
                    <a href={c.source_url} target="_blank" rel="noopener noreferrer"
                       className="text-xs text-brand-500 hover:underline truncate block">
                      {c.source_url}
                    </a>
                  )}
                </div>
                <span className="badge-gray text-xs flex-shrink-0">{c.source_type}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Tags */}
      <div className="mt-6 grid grid-cols-2 gap-4">
        <div>
          <p className="label">Routing Tags (Ghost)</p>
          <p className="text-sm text-gray-700 font-mono">
            {article.ghost_routing_tags === "[]" ? "None set" : article.ghost_routing_tags}
          </p>
        </div>
        <div>
          <p className="label">Category Tags (Ghost)</p>
          <p className="text-sm text-gray-700 font-mono">
            {article.ghost_category_tags === "[]" ? "None set" : article.ghost_category_tags}
          </p>
        </div>
      </div>

      {/* Rewrite button for non-review statuses */}
      {!isReviewable && article.status !== "published" && article.status !== "approved" && (
        <div className="mt-6 flex gap-3 pt-4 border-t border-gray-200">
          <button
            onClick={handleRewrite}
            className="btn-ghost btn"
            title="Queue this article for AI re-rewrite"
          >
            ↺ {article.status === "error" ? "Retry rewrite" : "Rewrite with AI"}
          </button>
        </div>
      )}

      {/* Action bar */}
      {isReviewable && (
        <div className="mt-6 flex gap-3 pt-4 border-t border-gray-200">
          <button onClick={handleApprove} disabled={saving} className="btn-primary btn">
            {saving ? "Publishing…" : "✓ Approve & Publish"}
          </button>
          <button onClick={handleApproveDraft} disabled={saving} className="btn-ghost btn">
            {saving ? "Saving…" : "Save as Draft"}
          </button>
          <button onClick={handleReject} className="btn-danger btn">
            ✕ Reject
          </button>
          <button onClick={handleRewrite} className="btn-ghost btn">
            ↺ Re-rewrite
          </button>
        </div>
      )}
      {article.status === "published" && article.ghost_publish_url && (
        <div className="mt-6 pt-4 border-t border-gray-200">
          <a href={article.ghost_publish_url} target="_blank" rel="noopener noreferrer"
             className="btn-primary btn inline-flex">
            View on Ghost →
          </a>
        </div>
      )}
    </div>
  );
}
